import { useRouter } from "next/router";
import axios from "axios";

import Evergreen from "features/Evergreen/Evergreen.component";
import SEO from "components/data-display/SEO/SEO.component";

import { GeolocationProvider } from "context/useGeolocation.context";
import { defaultLocation } from "data/defaultLocation";
import { getGenericVehicles } from "services/v1-slices/generic-vehicles-slice";
import { deepChangeObjectKeysCase } from "helpers/object-helpers";
import camelCase from "lodash/camelCase";
import { wrapper } from "store";
import { getVehicleSpecs } from "services/v1-slices/vehicle-specs-slice";
import { vinnV1Api } from "services/vinn-api-v1";
import cloneDeep from "lodash/cloneDeep";

export default function ResearchPage({ pageProps }) {
  const { location, genericVehicle, specs } = pageProps;
  const router = useRouter();
  const { make, model } = router.query;
  const carName = `${make} ${model}`;
  return (
    <>
      <SEO
        title={`Is the ${carName} a good car? | VINN`}
        description={`Is the ${carName} a good car? Learn everything about the ${carName} with VINN. Need more info? Talk to our Vehicle Expert team. We find cars for you.`}
        url={`https://www.vinnauto.com/research/${make}/${model}`}
      />
      <GeolocationProvider initialLocation={location}>
        <Evergreen genericVehicle={genericVehicle} specs={specs} />
      </GeolocationProvider>
    </>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, params }) => {
      let location = defaultLocation;
      if (req.headers["x-vercel-ip-country"] === "CA") {
        location = {
          city: req.headers["x-vercel-ip-city"],
          country: req.headers["x-vercel-ip-country"],
          province: req.headers["x-vercel-ip-country-region"],
          latitude: req.headers["x-vercel-ip-latitude"],
          longitude: req.headers["x-vercel-ip-longitude"],
        };
      }
      store.dispatch(getVehicleSpecs.initiate());
      // wait for request to be called on the server
      const [specs] = await Promise.all(
        store.dispatch(vinnV1Api.util.getRunningQueriesThunk()),
      );
      const makeId = specs.data.makes.find(
        (attr) => attr.name.toLowerCase() === params.make.toLowerCase(),
      )?.id;
      const model = specs.data.models.find(
        (attr) =>
          attr.name.toLowerCase() === params.model.toLowerCase() &&
          attr.makeId === makeId,
      );
      if (!model || !makeId || !model.evergreenPublishedAt) {
        return { notFound: true };
      }
      store.dispatch(
        getGenericVehicles.initiate({
          makeId,
          modelId: model?.id,
          sortBy: "year",
          orderBy: "desc",
          view: "research",
        }),
      );
      // wait for request to be called on the server - this depends on result of first
      const [GVResults] = await Promise.all(
        store.dispatch(vinnV1Api.util.getRunningQueriesThunk()),
      );
      const genericVehicle = cloneDeep(GVResults?.data?.data[0]);
      const newMedia = await Promise.all(
        genericVehicle.model.media.map(async (medium) => {
          try {
            let meta;
            if (medium.mediumType === "youtube") {
              meta = await axios.get(
                `https://youtube.com/oembed?url=${medium.url}&format=json`,
              );
            } else if (medium.mediumType === "tiktok") {
              meta = await axios.get(
                `https://www.tiktok.com/oembed?url=${medium.url}`,
              );
            }
            return {
              ...medium,
              ...deepChangeObjectKeysCase(meta?.data, camelCase),
            };
          } catch (_) {
            return medium;
          }
        }),
      );

      return {
        props: {
          location,
          specs: specs.data,
          genericVehicle: {
            ...genericVehicle,
            model: {
              ...genericVehicle.model,
              media: newMedia,
            },
          },
        },
      };
    },
);
